import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useOnboardingFlowTest } from './hooks/useOnboardingFlowTest';
import { useSolutionOrg } from '../config/useSolutionOrg';
import { getDashboardEntryRoute } from './dashboardEntryRoute';
import {
  isHomeDepotSetupPath,
  resolveHomeDepotSetupReturnPath,
} from './homeDepotEnrollmentIntent';

interface UseOnboardingRedirectPathOptions {
  redirectToPartnersForOnboardingFlowTestOff?: boolean;
}

export function useOnboardingRedirectPath({
  redirectToPartnersForOnboardingFlowTestOff = false,
}: UseOnboardingRedirectPathOptions = {}) {
  const location = useLocation();
  const { solutionOrgName } = useSolutionOrg();
  const dashboardEntryRoute = getDashboardEntryRoute(solutionOrgName);

  const {
    isTestReady: isOnboardingFlowTestReady,
    shouldSkipOnboarding,
    variationKey: onboardingFlowTestVariationKey,
  } = useOnboardingFlowTest({
    enabled: redirectToPartnersForOnboardingFlowTestOff,
  });

  const redirectPath = useMemo(() => {
    // Also check URL query params for returnPath
    const searchParams = new URLSearchParams(location.search);
    const returnPathFromUrl = searchParams.get('returnPath');
    const isHomeDepotSetupRoute = isHomeDepotSetupPath(location.pathname);
    const resolveReturnPath = (path: string) => (
      isHomeDepotSetupRoute ?
        resolveHomeDepotSetupReturnPath({
          redirectPath: path,
          fallbackPath: dashboardEntryRoute,
        }) :
        path
    );

    // Check state first
    if (location.state?.returnPath) {
      return resolveReturnPath(location.state?.returnPath);
    }

    // Check URL query params second
    if (returnPathFromUrl) {
      return resolveReturnPath(returnPathFromUrl);
    }

    if (redirectToPartnersForOnboardingFlowTestOff &&
      isOnboardingFlowTestReady &&
      onboardingFlowTestVariationKey &&
      !shouldSkipOnboarding) {
      return dashboardEntryRoute;
    }

    return dashboardEntryRoute;
  }, [
    location.state,
    location.search,
    location.pathname,
    dashboardEntryRoute,
    redirectToPartnersForOnboardingFlowTestOff,
    isOnboardingFlowTestReady,
    onboardingFlowTestVariationKey,
    shouldSkipOnboarding,
  ]);
  return redirectPath;
}
